import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Field } from 'redux-form'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import Input from 'components/common/FormElements/Input/Input'
import Columns from 'components/common/Columns/Columns'
import Media from 'components/common/Media'
import Theme from 'components/common/Theme/Theme'
import Mixin from 'components/common/Mixin'
import Text, { H2 } from 'components/common/Text/Text'
import { formatPhoneNumber, normalizePhoneNumber } from 'helpers/phone-number'
import model from 'components/AppointmentConfirmationPage/AppointmentInfo/viewModel'
import { StyledDrawer } from 'components/AppointmentConfirmationPage/Checkout/components/Common.styles'
import CheckoutDrawerTitle from 'components/AppointmentConfirmationPage/Checkout/components/CheckoutDrawerTitle'
import ThreeStepperDynamicTitle from '../ThreeStepperDynamicTitle'
import { SectionWrapper, ErrorMessage, ActionBarWrapper } from '../../../Stepper/components/common'
import { Heading } from '../common'
import {
  CONTACT_INFORMATION
} from 'components/AppointmentConfirmationPage/constants'
import StepperActionBar from '../../../Stepper/components/StepperActionBar'
import { KeepInContact, TermsAgreement } from 'components/Login/CreateAccount/CreateAccountFormPage/components/fields'
import { KEEP_IN_CONTACT_LABEL } from 'constant'
import { Separator } from 'components/common/Separator/Separator'
import { PRIVACY_POLICY_LINK, PRIVACY_POLICY_LABEL, APPOINTMENT_CHECKBOX_INPUT_COLOR_GREEN } from '../../constants'
import Icon from 'components/common/Icon/Icon'
import Spinner from 'components/common/Spinner'

dayjs.extend(customParseFormat)

const CustomInput = styled(Input)`
  width: 100%;

  & [data-error-message] {
    position: relative;
    top: 4px;
  }
`

const ContactInfoAndReview = ({
  activeVehicleName,
  address,
  currentStepIndex,
  dateAndTime,
  handleEditClick,
  handleSubmit,
  onPreviousStep,
  error,
  isDone,
  services,
  submitFailed,
  title,
  isE2EAppointment,
  subTitle,
  isEmailMe,
  activePromotions,
  showDetails,
  setShowDetails,
  isLoading
}) =>
  <form onSubmit={ handleSubmit }>
    {isLoading && <Spinner /> }
    <SectionWrapper>
      <Heading data-at-appointment-headline>{ title }</Heading>
      <SubHeadingWrapper>
        <StyledText>
          { subTitle }
        </StyledText>
        <StyledText>Click Edit to make changes. { !isE2EAppointment && 'We will confirm your request soon.' }</StyledText>
      </SubHeadingWrapper>
      <Columns adaptive width="50% 50%">
        <ColumnWrapper>
          <Media.Desktop><ContactInfoHeading noMargin>Contact Information</ContactInfoHeading></Media.Desktop>
          <Media.Mobile><ThreeStepperDynamicTitle contactInfoTitle title="Contact Information" isDone={ isDone } /></Media.Mobile>
          <fieldset>
            <legend><Media.ScreenReader>{ CONTACT_INFORMATION }</Media.ScreenReader></legend>
            <StyledField { ...model.firstName } component={ Input } />
            <StyledField { ...model.lastName } component={ Input } />
            <StyledField
              { ...model.phoneNumber }
              component={ CustomInput }
              formatValueDisplay={ formatPhoneNumber }
              normalize={ normalizePhoneNumber } />
            <CustomStyledField { ...model.email } component={ Input } />
          </fieldset>
        </ColumnWrapper>
        <StyledColumnWrapper>
          <CustomStyledDrawer
            drawerProps={{ CustomDrawerTitle: CheckoutDrawerTitle, title: 'Location', subtitle: address, noIcon: true, threeStepper: true }}
            withHeading
            editExpander
            complexTitle
            hideBorder
            onClick={ e => handleEditClick(e, 0) } />
          <StyledDrawer
            drawerProps={{ CustomDrawerTitle: CheckoutDrawerTitle, title: 'Service(s)', subtitle: services, noIcon: true, threeStepper: true }}
            withHeading
            editExpander
            complexTitle
            hideBorder
            onClick={ e => handleEditClick(e, 0) } />

          {Boolean(activePromotions.length) && (
            <PromotionsDrawer>
              <PromoSection>
                <FieldLabel>
                  Potential Savings
                </FieldLabel>
                {activePromotions.map(({
                  promotionId,
                  promotionShortDescription,
                  formattedExpiryDate,
                  termsAndConditions
                }) => (
                  <>
                    <PromoButton
                      key={ promotionId }
                      onClick={ (e) => {
                        e.preventDefault()
                        setShowDetails(!showDetails)
                      } }>
                      <PromoIcon asset="tire-promo" width="14px" alt="Promo Icon" />
                      <PromoWrapper>
                        <ShortDetailsWrapper>
                          <ShortDescription>
                            {promotionShortDescription}
                          </ShortDescription>
                          <ExpirationDate className="underline-hover">
                            Present offer in store. Expires {dayjs(formattedExpiryDate).format('MM/DD/YYYY')}
                          </ExpirationDate>
                        </ShortDetailsWrapper>
                      </PromoWrapper>

                      <DetailsLinkWrapper showDetails={ showDetails }>
                        {showDetails && (
                          <DetailsText className="underline-hover">
                            Hide
                          </DetailsText>
                        )}
                        <DetailsText className="underline-hover">
                          Details
                        </DetailsText>
                      </DetailsLinkWrapper>

                    </PromoButton>
                    {showDetails && (
                      <DetailsWrapper>
                        <TermsDetails>
                          <TermsDetailsLabel>
                            Terms & Conditions
                          </TermsDetailsLabel>
                          <TermsDetailsText>
                            {termsAndConditions}
                          </TermsDetailsText>
                        </TermsDetails>
                      </DetailsWrapper>
                    )}
                  </>
                ))}
              </PromoSection>
            </PromotionsDrawer>
          )}

          <StyledDrawer
            drawerProps={{ CustomDrawerTitle: CheckoutDrawerTitle, title: 'Date and Time', subtitle: dateAndTime, noIcon: true, threeStepper: true }}
            withHeading
            editExpander
            complexTitle
            hideBorder
            onClick={ e => handleEditClick(e, 1) } />
          <StyledDrawer
            drawerProps={{ CustomDrawerTitle: CheckoutDrawerTitle, title: 'Vehicle', subtitle: activeVehicleName, noIcon: true, threeStepper: true }}
            withHeading
            editExpander
            complexTitle
            hideBorder
            onClick={ e => handleEditClick(e, 1) } />
        </StyledColumnWrapper>
      </Columns>

      <CheckboxesWrapper>
        {!isEmailMe && (
          <>
            <KeepInContact
              form={ model }
              label={ KEEP_IN_CONTACT_LABEL }
              inputColor={ APPOINTMENT_CHECKBOX_INPUT_COLOR_GREEN } />
            <Separator margin="23px 0 28px" />
          </>
        )}
        <TermsAgreement
          form={ model }
          path={ PRIVACY_POLICY_LINK }
          linkLabel={ PRIVACY_POLICY_LABEL }
          inputColor={ APPOINTMENT_CHECKBOX_INPUT_COLOR_GREEN } />
      </CheckboxesWrapper>

      <ActionBarWrapper condensedBottom>
        <StepperActionBar
          hasPrevPage
          isPrevPageVisible
          alignRight
          currentStepIndex={ currentStepIndex }
          prevPageClick={ onPreviousStep }
          isE2EAppointment={ isE2EAppointment } />

        <ErrorMessage
          role="alert"
          aria-live="assertive"
          alignRight
          aria-label={ error }>
          { (submitFailed && error) || '' }
        </ErrorMessage>
      </ActionBarWrapper>
    </SectionWrapper>
  </form>

ContactInfoAndReview.propTypes = {
  activePromotions: PropTypes.arrayOf(),
  activeVehicleName: PropTypes.string,
  address: PropTypes.string,
  currentStepIndex: PropTypes.number,
  dateAndTime: PropTypes.string,
  error: PropTypes.string,
  handleEditClick: PropTypes.func,
  handleSubmit: PropTypes.func,
  isDone: PropTypes.bool,
  isE2EAppointment: PropTypes.bool,
  isEmailMe: PropTypes.bool,
  isLoading: PropTypes.bool,
  onPreviousStep: PropTypes.func,
  services: PropTypes.string,
  setShowDetails: PropTypes.func,
  showDetails: PropTypes.bool,
  submitFailed: PropTypes.bool,
  subTitle: PropTypes.string,
  title: PropTypes.string
}

export default ContactInfoAndReview

const StyledField = styled(Field)`
  display: block;
  margin-bottom: 50px;
  width: 100%;
`

const SubHeadingWrapper = styled.div`
  text-align: center;
`

const ContactInfoHeading = styled(H2)`
  ${Mixin.font.GothamBold(18)}
  margin: 10px 0 25px 0;
`

const ColumnWrapper = styled.div`
  padding: 0 40px;
  margin: 30px 0;
  ${Media.mobile`
    padding: 0 30px;
  `}
`

const StyledColumnWrapper = styled(ColumnWrapper)`
  background-color: ${Theme.colors.off_white};
  padding-top: 1px;
  padding-bottom: 40px;
`

const CustomStyledDrawer = styled(StyledDrawer)`
  margin-top: 25px;
`

const PromotionsDrawer = styled.div`
  margin-top: 45px;
  border-bottom: 1px solid #ddd;
`

const PromoSection = styled.h2`
  margin: 0;
`

const PromoButton = styled.button`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 0;
  border: none;
  color: ${Theme.colors.black_new};
  background: transparent;
  ${Media.desktop`
    &:hover .underline-hover {
      text-decoration: underline;
    }
  `}
`

const PromoIcon = styled(Icon)`
  position: absolute;
  left: -25px;
  top: 5px;
`

const PromoWrapper = styled.div`
  width: 80%;
  white-space: nowrap;
  text-transform: none;
`

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 20px 0 0 0;
`

const FieldLabel = styled.p`
  display: inline-block;
  margin: 0 0 5px 0;
  font-weight: 600;
`

const ShortDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 5px 0;
  width: 100%;
`

const ShortDescription = styled.p`
  margin: 0;
  padding: 0;
  font-weight: 600;
  ${Media.mobile`
    font-size: 12px;
  `}
`

const ExpirationDate = styled.p`
  margin: 0;
  padding: 0;
  font-size: 12px;
  ${Media.mobile`
    white-space: pre-line;
    text-align: start;
  `}
`

const DetailsLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  ${props => props.showDetails && css`
    & p:nth-child(1) {
      margin: 0;
    }

    ${Media.mobile`
      & p:nth-child(2) {
        margin: 0 0 5px 0;
      }
    `}
  `}
`

const DetailsText = styled.p`
  margin: 0 0 5px 0;
  padding: 0;
  color: #1262ac;
`

const TermsDetails = styled.div`
  display: flex;
  flex-direction: column;
`

const TermsDetailsLabel = styled.p`
  margin: 0;
  padding: 0 0 5px 0;
  font-weight: 600;
`

const TermsDetailsText = styled.p`
  margin: 0 0 30px 0;
  padding: 0;
  font-size: 12px;
`

const CustomStyledField = styled(StyledField)`
  margin-bottom: 30px;
`

const StyledText = styled(Text)`
  line-height: 20px;
  ${Media.desktop`
    display: block;
  `}
`

const CheckboxesWrapper = styled.div`
  max-width: 490px;
  padding: 0 40px;
`
